import { useMemo } from 'react';
import { useWorkQueueContext } from '../context/useWorkQueueContext.js';
import { getSeverityRank } from '../utils/severityUtils.js';
import { QUICK_FILTER_SEVERITIES, SEVERITY } from '../constants/severities.js';
import { DUE_BUCKET, STUDIO_OPTIONS } from '../constants/filterOptions.js';
import { matchesEventTypeFilter, eventIsCustomer } from '../utils/customerClassification.js';
import { compareByDueDate, countDueBuckets } from '../utils/dueBuckets.js';
import { SCOPE } from '../constants/mockEvents.js';

/** Prototype studio ids. GS fan-out fills `studio` from department/domain/sandbox. */
const HTL_STUDIO_SLUGS = new Set(STUDIO_OPTIONS.map((option) => option.value));

function isInboxCritical(event) {
  return event.severity === SEVERITY.CRITICAL || event.severity === SEVERITY.ACT_NOW;
}

function matchesFilters(event, filters, { skipInboxCritical = false } = {}) {
  if (filters.severity && event.severity !== filters.severity) return false;
  if (!skipInboxCritical && filters.inboxCriticalOnly && !isInboxCritical(event)) return false;
  if (filters.team.length && event.team && !filters.team.includes(event.team)) return false;
  if (filters.studio.length && !filters.studio.includes(event.studio)) return false;
  if (filters.type.length && !matchesEventTypeFilter(event, filters.type)) return false;
  if (filters.due) {
    // "Overdue" is a cross-cutting flag, not a bucket: overdue events keep their
    // real bucket (e.g. due_today) and set `overdue: true`. Match the flag for
    // Overdue; match the bucket for every other option.
    if (filters.due === DUE_BUCKET.OVERDUE) {
      if (!event.overdue) return false;
    } else if (event.dueBucket !== filters.due) {
      return false;
    }
  }
  if (filters.search) {
    const haystack = `${event.title} ${event.description} ${event.id} ${event.refId ?? ''}`.toLowerCase();
    if (!haystack.includes(filters.search.trim().toLowerCase())) return false;
  }
  return true;
}

function compareEvents(a, b, sort) {
  if (sort.by === 'severity') {
    const delta = getSeverityRank(a.severity) - getSeverityRank(b.severity);
    return delta !== 0 ? delta * (sort.direction === 'asc' ? -1 : 1) : 0;
  }
  if (sort.by === 'type') {
    return a.type.localeCompare(b.type) * (sort.direction === 'asc' ? 1 : -1);
  }
  return compareByDueDate(a, b, sort.direction);
}

export function scopeEventsForPersona(events, scope, persona) {
  const scoped = events.filter((event) => event.scope === scope);
  if (scope !== SCOPE.MY_TEAM) return scoped;
  if (persona?.role === 'individual') return [];
  if (persona?.role === 'manager' && persona.studio) {
    return scoped.filter((event) => {
      // Until CT-4867 ships a real studio id, live rows must not be gated by the
      // mock persona slug (`governance-operations`). Keep HTL-slug events filtered.
      if (!HTL_STUDIO_SLUGS.has(event.studio)) return true;
      return event.studio === persona.studio;
    });
  }
  return scoped;
}

/**
 * Filtered + sorted event list for a given scope, honoring inbox filters,
 * quick-chip severity, inbox Critical chip, search, sort and skip state.
 */
export function useEventList(scope) {
  const { events, loading, error, currentPersona, filters, sort, skippedIds, resolvedIds } =
    useWorkQueueContext();

  const derived = useMemo(() => {
    const personaScoped = scopeEventsForPersona(events, scope, currentPersona);
    const scoped = personaScoped.filter(
      (event) => !skippedIds.has(event.id) && !resolvedIds.has(event.id),
    );

    const counts = QUICK_FILTER_SEVERITIES.reduce((acc, sev) => {
      acc[sev] = scoped.filter((e) => e.severity === sev).length;
      return acc;
    }, {});

    // Per-type counts over the in-scope queue (before the type filter) so the
    // Event Type filter can offer the REAL types actually present, each with its
    // count — the mock TYPE_OPTIONS never matched the interim fan-out types.
    const typeCounts = scoped.reduce((acc, e) => {
      if (e.type) acc[e.type] = (acc[e.type] ?? 0) + 1;
      return acc;
    }, {});

    const customerCount = scoped.filter((e) => eventIsCustomer(e)).length;
    const dueCounts = countDueBuckets(scoped);

    const barFiltered = scoped.filter((e) => matchesFilters(e, filters, { skipInboxCritical: true }));
    const filtered = barFiltered
      .filter((e) => matchesFilters(e, filters))
      .sort((a, b) => compareEvents(a, b, sort));

    return {
      events: filtered,
      inboxListTotal: barFiltered.length,
      inboxCriticalCount: barFiltered.filter(isInboxCritical).length,
      counts,
      typeCounts,
      dueCounts,
      customerCount,
      total: scoped.length,
      teamScopeRestricted: scope === SCOPE.MY_TEAM && currentPersona?.role === 'individual',
    };
  }, [events, currentPersona, scope, filters, sort, skippedIds, resolvedIds]);

  // Loading/error come straight from the fetch in context; the filtered/sorted
  // list stays client-side for the interim fan-out (no unified server query yet).
  return { ...derived, loading, error };
}
